import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isArray from 'lodash/isArray'

const assistanceParams = (assistanceList) => {
  if (assistanceList === null) {
    return []
  }

  return assistanceList.map((item) => ({
    key: item.key,
    value: item.value,
    group: item.group,
    category: item.category,
    additional_info: get(item, 'additional_info'),
  }))
}

const destinationStationParams = (station) => {
  if (isEmpty(station)) {
    return null
  }

  return {
    name: station.name,
    name_code: station.name_code,
    crs_code: station.crs_code,
    category: station.category,
  }
}

const parseId = (id) => {
  if (id === null || id?.startsWith('$uid')) {
    return null
  }

  return id
}

const reservationSourceParams = (leg) => {
  const coach = get(leg, 'coach')
  const seats = get(leg, 'seats')

  if ((isEmpty(seats) || isEmpty(seats[0].id)) && isEmpty(coach) && leg.reservations.length === 0) {
    return null
  } else {
    return leg.reservation_source
  }
}

const reservationsParams = (leg) => {
  if (!isEmpty(leg.reservations) && !isArray(leg.seats)) {
    return leg.reservations
  }

  const coach = get(leg, 'coach')
  const seats = get(leg, 'seats')
  const source = get(leg, 'reservation_source')

  if ((isEmpty(seats) || isEmpty(seats[0].id)) && isEmpty(coach)) {
    return []
  }

  if (isEmpty(seats) && !isEmpty(coach)) {
    if (source === 'manual') {
      return [
        {
          coach: coach,
        },
      ]
    } else {
      return []
    }
  }

  return seats.map((seat) => ({
    seat: seat.id,
    units: seat.units,
    coach: coach,
    ticket_type: seat.ticket_type,
    tariff_code: seat.tariff_code,
    passenger_id: seat.passenger_id,
  }))
}

const reservationUpdatesParams = (updates) => {
  if (isEmpty(updates)) {
    return []
  }

  return updates.map((item) => ({
    action: item.action,
    journey: item.journey,
    leg_id: parseId(get(item, 'leg_id')),
    leg_position: item.leg_position,
  }))
}

const validationReminderParams = (validation) => {
  if (isEmpty(validation)) {
    return null
  }

  return {
    accessible: validation.accessible,
    alternative_transport_arranged: validation.alternative_transport_arranged,
    assistance_available: validation.assistance_available,
    disruption: validation.disruption,
  }
}

const legParams = (legs) => {
  return legs.map((item) => ({
    id: parseId(get(item, 'id')),
    rsid: item.rsid,
    headcode: item.head_code,
    train_uid: item.train_uid,
    train_rid: item.train_rid,
    position: item.position,
    depart_assignee: item.depart_assignee,
    arrival_assignee: item.arrival_assignee,
    overall_position: item.overall_position,
    alight_station_code: get(item, 'alight_station.crs_code'),
    board_station_code: get(item, 'board_station.crs_code'),
    destination_scheduled_arrival: item.destination_scheduled_arrival,
    destination_station_code: get(item, 'destination_station.crs_code'),
    mode: item.mode,
    origin_scheduled_departure: item.origin_scheduled_departure,
    origin_station_code: get(item, 'origin_station.crs_code'),
    realtime_arrival: item.realtime_arrival,
    realtime_classification: item.realtime_classification,
    realtime_departure: item.realtime_departure,
    scheduled_arrival: item.scheduled_arrival,
    scheduled_departure: item.scheduled_departure,
    operator_code: get(item, 'operator.atoc_code'),
    meeting_point: meetingPointParams(item.meeting_point, item.overall_position || item.position),
    departure_meeting_point: item.departure_meeting_point,
    arrival_meeting_point: item.arrival_meeting_point,
    departure_assistances: assistanceParams(item.departure_assistances),
    arrival_assistances: assistanceParams(item.arrival_assistances),
    on_board_assistances: assistanceParams(item.on_board_assistances),
    reservations: reservationsParams(item),
    rars_leg_id: item.rars_leg_id,
    reservation_source: reservationSourceParams(item),
    service_identifier: item.service_identifier,
    origin_platform: item.origin_platform,
    destination_platform: item.destination_platform,
    luggages: item.luggages,
  }))
}

const meetingPointParams = (meetingPoint, position) => {
  if (meetingPoint === undefined && position === 1) {
    return {
      station_meeting_point_id: null,
      other_text: null,
    }
  }
  return meetingPoint
}

const journeyParams = (journeys) => {
  const journeysWithLegs = journeys.filter((journey) => journey.legs.length > 0)

  return journeysWithLegs.map((item) => ({
    id: parseId(get(item, 'id')),
    bespoke: item.bespoke,
    direction: item.direction,
    duration: item.duration,
    destination_station: destinationStationParams(item.destination_station),
    origin_station_code: item.origin_station.crs_code,
    destination_station_code:
      // eslint-disable-next-line camelcase
      item.destination_station?.crs_code || item.destination_station_id,
    realtime_arrival: item.realtime_arrival,
    realtime_classification: item.realtime_classification,
    realtime_departure: item.realtime_departure,
    scheduled_arrival: item.scheduled_arrival,
    scheduled_departure: item.scheduled_departure,
    outbound_time_type: item.outbound_time_type,
    min_change_time: item.min_change_time,
    legs: legParams(item.legs),
    reservation_updates: reservationUpdatesParams(item.reservation_updates),
  }))
}

export default function bookingParams(booking, extraParams = { recurring: false }) {
  return {
    customer_ref: booking.customer_ref,
    source: booking.source,
    journeys: journeyParams(booking.journeys),
    rars_ref: booking.rars_ref,
    recurring: extraParams.recurring,
    validation_reminder: validationReminderParams(booking.validation_reminder),
    validation_reminder_enabled: booking.validation_reminder_enabled,
  }
}
